import { getMonthName, parseDbTimestamp, pickT } from "@repo/shared";
import type { ClientFileType } from "@repo/shared/types";
import { type ClassValue, clsx } from "clsx";
import { formatDistanceToNow } from "date-fns";
import { FileAudio, FileText, ImageIcon, type LucideIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { T } from "./utils.i18n";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getFileTypeIcon(type: ClientFileType): LucideIcon {
  if (type === "image") return ImageIcon;
  if (type === "audio") return FileAudio;
  return FileText;
}

/**
 * date-fns `formatDistanceToNow` of a stored DB timestamp; "" when it cannot
 * be parsed.
 */
export function distanceToNowFromDb(
  value: string,
  options?: Parameters<typeof formatDistanceToNow>[1],
): string {
  const d = parseDbTimestamp(value);
  return d ? formatDistanceToNow(d, options) : "";
}

/**
 * QL-494 — local calendar days from `now` to a server timestamp: 0 for today,
 * -1 for yesterday, 1 for tomorrow. Null when the timestamp doesn't parse.
 */
export function localCalendarDayOffset(date: string | Date, now: Date = new Date()): number | null {
  const target = date instanceof Date ? date : parseDbTimestamp(date);
  if (!target || Number.isNaN(target.getTime())) return null;
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  // Round, not floor: a day across a DST change is 23 or 25 hours long.
  return Math.round((startOfDay(target) - startOfDay(now)) / 86_400_000);
}

/**
 * Language-driven month + year (e.g. "Aug 2026", "august 2026"). Month NAMES
 * follow the UI language (translation, not regional settings — QL-108). Month
 * precision matches the app's month-picker inputs; UTC pins first-of-month
 * values to the intended month in every timezone, so callers constructing
 * default dates must build them via `Date.UTC(...)` rather than local time.
 */
export function formatMonthYear(
  date: Date | string,
  language: string,
  opts?: { monthStyle?: "short" | "long" },
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  return `${getMonthName(d.getUTCMonth(), language, opts?.monthStyle ?? "short")} ${d.getUTCFullYear()}`;
}

export function getRelativeTime(date: Date | string, lang: string = "en_GB"): string {
  const t = pickT(T, lang);

  // PostgreSQL timestamps have no timezone marker — parse them as UTC
  const parsed = date instanceof Date ? date : parseDbTimestamp(date);
  if (!parsed) return "";
  const now = new Date();
  const diff = now.getTime() - parsed.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) return t.justNow;
  if (minutes < 60) return `${minutes} ${minutes !== 1 ? t.minutesAgo : t.minuteAgo}`;
  if (hours < 24) return `${hours} ${hours !== 1 ? t.hoursAgo : t.hourAgo}`;
  if (days < 30) return `${days} ${days !== 1 ? t.daysAgo : t.dayAgo}`;
  if (months < 12) return `${months} ${months !== 1 ? t.monthsAgo : t.monthAgo}`;
  return `${years} ${years !== 1 ? t.yearsAgo : t.yearAgo}`;
}
